import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/layout/Navbar';
import SEO from './components/common/SEO';
import ScrollToTop from './components/common/ScrollToTop';
import { preloadImages } from './utils/imageOptimization';
import './App.css';

// ===== CRITICAL PATH - Load immediately =====
// ===== CRITICAL UI BASE - Load immediately =====
import Chatbot from './components/chatbot/Chatbot';
import NotificationManager from './components/common/NotificationManager';
import ProtectedOnboardingRoute from './components/routes/ProtectedOnboardingRoute';
import Footer from './components/layout/Footer';
import MobileBottomNav from './components/layout/MobileBottomNav';

// ===== LAZY LOADED PAGES - Code splitting for better bundle size =====
// Safe lazy loading with error handling
const createLazyComponent = (importFn, name) => 
  React.lazy(() => 
    importFn().catch(err => {
      console.error(`Failed to load ${name}:`, err);
      throw err;
    })
  );

const LandingPage = createLazyComponent(() => import('./pages/LandingPage'), 'LandingPage');
const Login = createLazyComponent(() => import('./pages/auth/Login'), 'Login');
const Register = createLazyComponent(() => import('./pages/auth/Register'), 'Register');
const OTPLogin = createLazyComponent(() => import('./pages/auth/OTPLogin'), 'OTPLogin');
const ContactPage = createLazyComponent(() => import('./pages/ContactPage'), 'ContactPage');
const About = createLazyComponent(() => import('./pages/About'), 'About');
const NotFound = createLazyComponent(() => import('./pages/NotFound'), 'NotFound');

// Admin pages
const AdminDashboard = createLazyComponent(() => import('./pages/dashboard/admin/AdminDashboard'), 'AdminDashboard');
const AdminIncome = createLazyComponent(() => import('./pages/AdminIncome'), 'AdminIncome');
const CategoryRequests = createLazyComponent(() => import('./pages/admin/CategoryRequests'), 'CategoryRequests');
const AdminCategories = createLazyComponent(() => import('./pages/AdminCategories'), 'AdminCategories');

// User/Worker dashboards
const UserDashboard = createLazyComponent(() => import('./pages/UserDashboard'), 'UserDashboard');
const WorkerDashboard = createLazyComponent(() => import('./pages/WorkerDashboard'), 'WorkerDashboard');

// Payment pages
const WorkerSubscriptionPayment = createLazyComponent(() => import('./pages/WorkerSubscriptionPayment'), 'WorkerSubscriptionPayment');
const PaymentSuccess = createLazyComponent(() => import('./pages/PaymentSuccess'), 'PaymentSuccess');

// Worker onboarding - NEW UNIFIED FLOW
const WorkerOnboardingFlow = createLazyComponent(() => import('./components/worker-onboarding/WorkerOnboardingFlow'), 'WorkerOnboardingFlow');

// Workers pages
const WorkersList = createLazyComponent(() => import('./pages/WorkersList'), 'WorkersList');
const WorkerDetail = createLazyComponent(() => import('./pages/WorkerDetail'), 'WorkerDetail');
const BecomeWorker = createLazyComponent(() => import('./pages/BecomeWorker'), 'BecomeWorker');

// Subscription
const SubscriptionPage = createLazyComponent(() => import('./pages/SubscriptionPage'), 'SubscriptionPage');

// Informational pages
const PrivacyPolicy = createLazyComponent(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const TermsOfService = createLazyComponent(() => import('./pages/TermsOfService'), 'TermsOfService');

// MFA pages
const AdminMFASetup = createLazyComponent(() => import('./pages/AdminMFASetup'), 'AdminMFASetup');
const AdminMFAVerify = createLazyComponent(() => import('./pages/AdminMFAVerify'), 'AdminMFAVerify');
const WorkerMFAVerify = createLazyComponent(() => import('./pages/WorkerMFAVerify'), 'WorkerMFAVerify');

// Fallback component for loading states - Minimal to prevent flicker
const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-10 h-10 border-3 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
  </div>
);


function LayoutWrapper() {
  const location = useLocation();

  // Define routes where public components should be HIDDEN
  const isAdminPath = location.pathname.startsWith('/admin') || location.pathname.startsWith('/worker/dashboard') || location.pathname.startsWith('/user/dashboard');
  const isAuthPath = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/login-otp';

  // Public Navbar is hidden on Admin routes
  const showNavbar = !location.pathname.startsWith('/admin');

  // Public Footer is hidden on Admin and Auth routes
  const showFooter = !isAdminPath && !isAuthPath;

  // Bottom Nav should be hidden on Admin and specific routes
  const showBottomNav = !location.pathname.startsWith('/admin');


  return (
    <div className="App relative min-h-screen flex flex-col">
      {showNavbar && <Navbar />}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {/* Add padding bottom on mobile for Bottom Nav */}
      <main className={`flex-grow ${showBottomNav ? 'pb-20 md:pb-0' : ''}`}>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login-otp" element={<OTPLogin />} />
            <Route path="/register" element={<Register />} />

            {/* Legacy redirect to prevent slug match */}
            <Route path="/worker-dashboard" element={<Navigate to="/worker/dashboard" replace />} />

            <Route path="/workers" element={<WorkersList />} />
            <Route path="/:slug" element={<WorkerDetail />} />
            <Route path="/become-worker" element={<BecomeWorker />} />

            {/* New Unified Worker Onboarding Flow - Protected */}
            <Route
              path="/worker/onboarding"
              element={
                <ProtectedOnboardingRoute>
                  <WorkerOnboardingFlow />
                </ProtectedOnboardingRoute>
              }
            />

            <Route path="/worker/subscription" element={<SubscriptionPage />} />
            <Route path="/worker/subscription-payment" element={<WorkerSubscriptionPayment />} />
            <Route path="/worker/mfa-verify" element={<WorkerMFAVerify />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />

            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

            <Route path="/admin-dashboard/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/mfa-setup" element={<AdminMFASetup />} />
            <Route path="/admin/mfa-verify" element={<AdminMFAVerify />} />
            <Route path="/admin/income" element={<AdminIncome />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/category-requests" element={<CategoryRequests />} />

            <Route path="/user/dashboard" element={<UserDashboard />} />
            <Route path="/worker/dashboard" element={<WorkerDashboard />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isAdminPath && <Chatbot />}
      <NotificationManager />
      {showFooter && <Footer />}
      <MobileBottomNav />
    </div>
  );
}

function App() {
  // Preload critical images (non-favicon resources)
  useEffect(() => {
    preloadImages([]);
  }, []);

  return (
    <AuthProvider>
      <SocketProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <SEO />
          <ScrollToTop />
          <LayoutWrapper />
        </Router>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
